import { ApiService } from "@/lib/api"
import type { PaymentMethod } from "@/store/appointment-store"
import type { AppointmentStore } from "@/store/appointment-store"
import type { PaymentMethodsConfig, PublicPaymentSettings } from "@/lib/types/settings.types"

export type PaymentMethodSettingKey = "cash" | "bankTransfer" | "card" | "wallet"

const SETTING_TO_APPOINTMENT: Record<PaymentMethodSettingKey, PaymentMethod> = {
  cash: "Cash",
  bankTransfer: "Transfer",
  card: "Card",
  wallet: "Cash",
}

const APPOINTMENT_TO_SETTING: Partial<Record<PaymentMethod, PaymentMethodSettingKey>> = {
  Cash: "cash",
  Transfer: "bankTransfer",
  Card: "card",
}

export const DEFAULT_PAYMENT_METHODS_CONFIG: PaymentMethodsConfig = {
  cash: {
    enabled: true,
    label: "Pay at venue",
    description: "Pay with cash at the salon",
    depositPercentage: 0,
  },
  bankTransfer: {
    enabled: true,
    label: "Transfer",
    description: "Pay via direct bank transfer",
    mode: "manual",
    paystackDynamic: { expiryMinutes: 30 },
    manual: { bankName: "", accountNumber: "", accountName: "" },
  },
  card: {
    enabled: true,
    label: "Card",
    description: "Pay with debit/credit card",
    activeProvider: "paystack",
    providers: {
      paystack: { enabled: true },
      stripe: { enabled: false },
      flutterwave: { enabled: false },
      razorpay: { enabled: false },
    },
  },
  wallet: {
    enabled: true,
    label: "Wallet Balance",
    description: "Pay using your wallet balance",
  },
}

export function mergePaymentMethodsConfig(
  existing?: Record<string, unknown> | PaymentMethodsConfig | null,
): PaymentMethodsConfig {
  const base = structuredClone(DEFAULT_PAYMENT_METHODS_CONFIG)
  if (!existing || typeof existing !== "object") return base

  const legacy = existing as Record<string, unknown>

  if (legacy.cash && typeof legacy.cash === "object") {
    Object.assign(base.cash, legacy.cash)
    const deposit = Number(base.cash.depositPercentage)
    base.cash.depositPercentage = Number.isFinite(deposit) ? deposit : 0
  }

  if (legacy.bankTransfer && typeof legacy.bankTransfer === "object") {
    const bt = legacy.bankTransfer as Record<string, unknown>
    Object.assign(base.bankTransfer, bt)
    if (!base.bankTransfer.mode) base.bankTransfer.mode = "manual"
    base.bankTransfer.paystackDynamic = {
      expiryMinutes:
        (bt.paystackDynamic as { expiryMinutes?: number } | undefined)?.expiryMinutes ?? 30,
    }
    base.bankTransfer.manual = {
      bankName: (bt.manual as { bankName?: string } | undefined)?.bankName ?? "",
      accountNumber:
        (bt.manual as { accountNumber?: string } | undefined)?.accountNumber ?? "",
      accountName:
        (bt.manual as { accountName?: string } | undefined)?.accountName ?? "",
    }
  }

  if (legacy.card && typeof legacy.card === "object") {
    const card = legacy.card as Record<string, unknown>
    Object.assign(base.card, card)
    if (!base.card.activeProvider) {
      const providers = (card.providers as string[] | undefined) ?? ["paystack"]
      base.card.activeProvider =
        (providers[0] as PaymentMethodsConfig["card"]["activeProvider"]) ?? "paystack"
    }
    if (Array.isArray(card.providers)) {
      const list = card.providers as string[]
      for (const id of ["paystack", "stripe", "flutterwave", "razorpay"] as const) {
        base.card.providers[id] = { enabled: list.includes(id) }
      }
    } else if (card.providers && typeof card.providers === "object") {
      Object.assign(base.card.providers, card.providers)
    }
  }

  if (legacy.wallet && typeof legacy.wallet === "object") {
    Object.assign(base.wallet!, legacy.wallet)
  }

  return base
}

export function settingKeyToAppointmentMethod(
  key: PaymentMethodSettingKey,
): PaymentMethod | null {
  return SETTING_TO_APPOINTMENT[key] ?? null
}

export function appointmentMethodToSettingKey(
  method: PaymentMethod,
): PaymentMethodSettingKey | null {
  return APPOINTMENT_TO_SETTING[method] ?? null
}

export function getEnabledBookingMethods(
  config: PaymentMethodsConfig,
): Array<{
  key: PaymentMethodSettingKey
  method: PaymentMethod
  label: string
  description?: string
}> {
  const keys: PaymentMethodSettingKey[] = ["cash", "bankTransfer", "card"]
  return keys
    .filter((key) => {
      const entry = config[key]
      return entry && "enabled" in entry && entry.enabled
    })
    .map((key) => ({
      key,
      method: SETTING_TO_APPOINTMENT[key],
      label: config[key]?.label ?? key,
      description: config[key]?.description,
    }))
}

export function syncSelectedPaymentMethod(
  store: AppointmentStore,
  config: PaymentMethodsConfig,
): void {
  const methods = getEnabledBookingMethods(config)
  if (
    methods.length > 0 &&
    !methods.some((m) => m.method === store.selectedPaymentMethod)
  ) {
    store.setSelectedPaymentMethod(methods[0].method)
  }
}

export function configFromPublicPaymentSettings(
  settings: PublicPaymentSettings,
): PaymentMethodsConfig {
  return mergePaymentMethodsConfig(settings.paymentMethods)
}

export async function loadPublicPaymentMethodsConfig(): Promise<PaymentMethodsConfig> {
  const settings = await ApiService.getPublicPaymentSettings()
  return configFromPublicPaymentSettings(settings)
}

export async function refreshAppointmentPaymentMethods(
  store: AppointmentStore,
): Promise<PaymentMethodsConfig> {
  const config = await loadPublicPaymentMethodsConfig()
  store.setPaymentMethodsConfig(config)
  syncSelectedPaymentMethod(store, config)
  return config
}

export function formatPaymentMethodHint(
  config: PaymentMethodsConfig,
  key: PaymentMethodSettingKey,
  totalAmount?: number,
): string | undefined {
  if (key === "cash") {
    const pct = config.cash.depositPercentage ?? 0
    if (pct > 0 && totalAmount != null && totalAmount > 0) {
      const deposit = Math.round((totalAmount * pct) / 100)
      const remaining = totalAmount - deposit
      return `Pay ${pct}% now (${formatNgn(deposit)}) to secure your booking${
        remaining > 0 ? `; ${formatNgn(remaining)} due at the venue.` : "."
      }`
    }
    if (pct > 0) {
      return `Pay ${pct}% upfront to secure your booking; the rest is due at the venue.`
    }
    return config.cash.description
  }

  if (key === "bankTransfer") {
    if (config.bankTransfer.mode === "paystack_dynamic") {
      const mins = config.bankTransfer.paystackDynamic?.expiryMinutes ?? 30
      return (
        config.bankTransfer.description ??
        `You will receive a temporary bank account valid for about ${mins} minutes.`
      )
    }
    const manual = config.bankTransfer.manual
    if (manual?.bankName && manual?.accountNumber) {
      const accountLine = `Transfer to ${manual.bankName} · ${manual.accountNumber}${
        manual.accountName ? ` (${manual.accountName})` : ""
      }`
      // Prefer account details; keep admin description as an extra line when it adds info.
      const description = config.bankTransfer.description?.trim()
      if (
        description &&
        description.toLowerCase() !== "pay via direct bank transfer" &&
        !description.includes(manual.accountNumber)
      ) {
        return `${description}\n${accountLine}`
      }
      return accountLine
    }
    return config.bankTransfer.description
  }

  if (key === "card") {
    return config.card.description
  }

  return undefined
}

function formatNgn(amount: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(amount)
}

export function isPaymentMethodEnabled(
  config: PaymentMethodsConfig,
  method: PaymentMethod,
): boolean {
  const key = appointmentMethodToSettingKey(method)
  if (!key) return false
  const entry = config[key]
  return Boolean(entry && "enabled" in entry && entry.enabled)
}
